import {
  mdiAccountRemoveOutline,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiClockAlertOutline,
  mdiHelpCircleOutline,
  mdiTrophyOutline,
} from '@quasar/extras/mdi-v7';
import { enrollmentStatusColor } from './enrollmentStatusColor';

/**
 * Everything the UI needs to present one enrollment status: the Quasar color
 * (shared with the teacher-facing chips via `enrollmentStatusColor`), an icon,
 * and the i18n keys for its label, its meaning, and what the student can do
 * next.
 *
 * This is the single source of truth — the dashboard chip and the explanation
 * dialog both read it, so a wording or color change lands in one place.
 */
export interface EnrollmentStatusMeta {
  /** Normalized status, or 'unknown' when the backend sent something else. */
  key: string;
  color: string;
  icon: string;
  /** i18n key for the short label rendered on the chip. */
  labelKey: string;
  /** i18n key for "what this status means". */
  messageKey: string;
  /** i18n key for "what you can do about it". */
  actionKey: string;
}

const META: Record<string, EnrollmentStatusMeta> = {
  active: {
    key: 'active',
    color: enrollmentStatusColor('active'),
    icon: mdiCheckCircleOutline,
    labelKey: 'SCHOOL_STATUS_ACTIVE',
    messageKey: 'ENROLLMENT_STATUS_ACTIVE_MESSAGE',
    actionKey: 'ENROLLMENT_STATUS_ACTIVE_ACTION',
  },
  pending: {
    key: 'pending',
    color: enrollmentStatusColor('pending'),
    icon: mdiClockAlertOutline,
    labelKey: 'SCHOOL_STATUS_PENDING',
    messageKey: 'ENROLLMENT_STATUS_PENDING_MESSAGE',
    actionKey: 'ENROLLMENT_STATUS_PENDING_ACTION',
  },
  completed: {
    key: 'completed',
    color: enrollmentStatusColor('completed'),
    icon: mdiTrophyOutline,
    labelKey: 'SCHOOL_STATUS_COMPLETED',
    messageKey: 'ENROLLMENT_STATUS_COMPLETED_MESSAGE',
    actionKey: 'ENROLLMENT_STATUS_COMPLETED_ACTION',
  },
  dropped: {
    key: 'dropped',
    color: enrollmentStatusColor('dropped'),
    icon: mdiAccountRemoveOutline,
    labelKey: 'SCHOOL_STATUS_DROPPED',
    messageKey: 'ENROLLMENT_STATUS_DROPPED_MESSAGE',
    actionKey: 'ENROLLMENT_STATUS_DROPPED_ACTION',
  },
  declined: {
    key: 'declined',
    color: enrollmentStatusColor('declined'),
    icon: mdiCloseCircleOutline,
    labelKey: 'SCHOOL_STATUS_DECLINED',
    messageKey: 'ENROLLMENT_STATUS_DECLINED_MESSAGE',
    actionKey: 'ENROLLMENT_STATUS_DECLINED_ACTION',
  },
};

const UNKNOWN_META: EnrollmentStatusMeta = {
  key: 'unknown',
  color: enrollmentStatusColor(''),
  icon: mdiHelpCircleOutline,
  labelKey: 'STATUS',
  messageKey: 'ENROLLMENT_STATUS_UNKNOWN_MESSAGE',
  actionKey: 'ENROLLMENT_STATUS_UNKNOWN_ACTION',
};

export function enrollmentStatusMeta(status?: string): EnrollmentStatusMeta {
  return META[(status || '').toLowerCase()] || UNKNOWN_META;
}
